import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"
import { moveAnnotationsToBack } from "../lib/apexcharts_annotations"

// 並列リクエスト制御キュー
class RequestQueue {
  constructor(concurrency = 3) {
    this.concurrency = concurrency
    this.running = 0
    this.queue = []
  }

  async enqueue(fn) {
    if (this.running >= this.concurrency) {
      await new Promise(resolve => this.queue.push(resolve))
    }
    this.running++
    try {
      return await fn()
    } finally {
      this.running--
      if (this.queue.length > 0) this.queue.shift()()
    }
  }
}

const TILE_ACTIVE_CLASSES = ["border-t-primary", "bg-primary/5"]
const TILE_INACTIVE_CLASSES = ["border-t-base-300"]

export default class extends Controller {
  static targets = [
    "chart", "keywordCheckbox", "keywordRow",
    "chips", "chipsEmpty", "dropdown", "dropdownWrap",
    "metricTile", "modeButton", "sovPanel", "sovBars", "sovMetricLabel",
    "trendTitle", "trendMetricLabel", "searchInput", "selectionCounter"
  ]
  static values = {
    twDataUrl: String,
    instDataUrl: String,
    fromDate: String,
    toDate: String,
    weekends: Array,
    maxKeywords: { type: Number, default: 10 }
  }

  connect() {
    this.series = new Map()
    this.abortControllers = new Map()
    this.instQueue = new RequestQueue(3)
    this.mode = "actual"
    this.activeMetrics = new Set(["posts"])
    this.shareMetric = "posts"
    this.metricLabels = {
      posts: '投稿数',
      estimated_reach: '推定リーチ数',
      reposts: 'リポスト数',
      likes: 'いいね数'
    }
    this.metricDashArrays = {
      posts: 0,
      estimated_reach: 6,
      reposts: 2,
      likes: 6
    }
    this.palette = ['#167bff', '#f31260', '#0bbf58', '#f59e0b', '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6', '#f97316', '#6366f1']
    this.onDocumentClick = this.closeDropdownOnOutsideClick.bind(this)
    document.addEventListener('click', this.onDocumentClick)
    this.initializeChart()
    this.updateSummaries()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
    document.removeEventListener('click', this.onDocumentClick)
    this.abortControllers.forEach(controller => controller.abort())
    this.abortControllers.clear()
  }

  initializeChart() {
    const annotations = {
      xaxis: (this.weekendsValue || []).map(weekend => ({
        x: weekend.from,
        x2: weekend.to,
        fillColor: weekend.color,
        opacity: 0.25
      }))
    }
    const isShare = this.mode === "share"

    const options = {
      chart: {
        type: isShare ? 'area' : 'line',
        stacked: isShare,
        height: '100%',
        id: 'keywordComparisonChart',
        fontFamily: 'inherit',
        animations: { enabled: false },
        events: {
          mounted: () => moveAnnotationsToBack(this.chartTarget),
          updated: () => moveAnnotationsToBack(this.chartTarget)
        },
        toolbar: {
          show: true,
          tools: { download: true, zoom: true, zoomin: false, zoomout: false, pan: false, reset: true }
        }
      },
      series: [],
      xaxis: {
        type: 'datetime',
        timezone: 'Asia/Tokyo',
        min: Date.parse(this.fromDateValue),
        max: Date.parse(this.toDateValue),
        labels: {
          show: true,
          formatter: function(value) {
            const date = new Date(value)
            return isNaN(date.getTime()) ? value : `${date.getMonth() + 1}/${date.getDate()}`
          },
          datetimeUTC: false,
          style: { colors: '#374151', fontSize: '12px', fontFamily: 'inherit' }
        },
        axisBorder: { show: true, color: '#E5E7EB' },
        axisTicks: { show: true, color: '#E5E7EB' }
      },
      yaxis: this.currentYAxisOptions(),
      legend: {
        // シェアモードはチップバーとSOVカードが凡例を兼ねるため非表示
        show: !isShare,
        showForSingleSeries: true,
        position: 'bottom',
        horizontalAlign: 'center'
      },
      colors: this.palette,
      fill: isShare ? { type: 'solid', opacity: 0.75 } : { type: 'solid', opacity: 1 },
      tooltip: {
        theme: 'light',
        shared: true,
        intersect: false,
        x: {
          formatter: function(value) {
            const date = new Date(value)
            return isNaN(date.getTime()) ? value : `${date.getMonth() + 1}/${date.getDate()}`
          }
        },
        y: {
          formatter: isShare ?
            function(value) { return `${(value ?? 0).toFixed(1)}%` } :
            function(value) { return new Intl.NumberFormat('ja-JP').format(value) }
        }
      },
      dataLabels: { enabled: false },
      stroke: { width: isShare ? 1 : 2, curve: 'straight' },
      grid: { borderColor: '#E5E7EB', strokeDashArray: 3 },
      noData: { text: 'キーワードを選択してください', style: { fontSize: '16px', color: '#6B7280' } },
      annotations: annotations
    }

    this.chart = new ApexCharts(this.chartTarget, options)
    this.chart.render()
  }

  // --- イベントハンドラー ---

  keywordChanged(event) {
    const checkbox = event.target
    const keywordId = checkbox.dataset.keywordId
    const platform = checkbox.dataset.platform

    if (checkbox.checked) {
      const checkedCount = this.keywordCheckboxTargets.filter(cb => cb.checked).length
      if (checkedCount > this.maxKeywordsValue) {
        checkbox.checked = false
        alert(`キーワードは${this.maxKeywordsValue}件まで選択可能です`)
        return
      }
      this.loadKeywordData(keywordId, platform)
    } else {
      this.removeKeywordData(keywordId)
    }
    this.updateSummaries()
  }

  selectAll() {
    this.keywordCheckboxTargets.forEach(cb => {
      if (cb.checked) return

      const checkedCount = this.keywordCheckboxTargets.filter(c => c.checked).length
      if (checkedCount >= this.maxKeywordsValue) return

      cb.checked = true
      this.loadKeywordData(cb.dataset.keywordId, cb.dataset.platform)
    })
    this.updateSummaries()
  }

  removeKeyword(keywordId) {
    const checkbox = this.keywordCheckboxTargets.find(cb => cb.dataset.keywordId === keywordId)
    if (checkbox) checkbox.checked = false
    this.removeKeywordData(keywordId)
    this.updateSummaries()
  }

  toggleDropdown(event) {
    event.stopPropagation()
    this.dropdownTarget.classList.toggle('hidden')
  }

  closeDropdownOnOutsideClick(event) {
    if (!this.hasDropdownTarget || this.dropdownTarget.classList.contains('hidden')) return
    if (this.dropdownWrapTarget.contains(event.target)) return

    this.dropdownTarget.classList.add('hidden')
  }

  langChanged(event) {
    const lang = event.target.value
    const instCheckboxes = this.keywordCheckboxTargets.filter(cb => cb.dataset.platform === 'instagram' && cb.checked)
    instCheckboxes.forEach(cb => {
      const keywordId = cb.dataset.keywordId
      this.removeKeywordData(keywordId)
      this.loadKeywordData(keywordId, 'instagram', lang)
    })
  }

  modeChanged(event) {
    const newMode = event.currentTarget.dataset.mode
    if (newMode === this.mode) return

    this.mode = newMode
    this.modeButtonTargets.forEach(button => {
      button.classList.toggle('btn-active', button.dataset.mode === newMode)
    })
    this.chart.destroy()
    this.initializeChart()
    this.refreshChart()
  }

  tileClicked(event) {
    const metric = event.currentTarget.dataset.metric
    if (this.mode === "share") {
      if (this.shareMetric === metric) return
      this.shareMetric = metric
    } else {
      if (this.activeMetrics.has(metric)) {
        if (this.activeMetrics.size <= 1) return
        this.activeMetrics.delete(metric)
      } else {
        this.activeMetrics.add(metric)
      }
    }
    this.refreshChart()
  }

  searchChanged() {
    const query = (this.searchInputTarget.value || "").trim()
    this.keywordRowTargets.forEach(row => {
      const name = row.dataset.keywordName || ""
      row.classList.toggle('hidden', query.length > 0 && !name.includes(query))
    })
  }

  // --- データ取得 ---

  loadKeywordData(keywordId, platform, lang = null) {
    if (platform === 'twitter') {
      this.fetchTwData([keywordId])
    } else {
      this.fetchInstData(keywordId, lang)
    }
  }

  async fetchTwData(keywordIds) {
    const params = new URLSearchParams()
    keywordIds.forEach(id => params.append('keyword_ids[]', id))
    params.append('fromDate', this.fromDateValue)
    params.append('toDate', this.toDateValue)

    keywordIds.forEach(id => this.setLoading(id, true))

    try {
      const response = await fetch(`${this.twDataUrlValue}?${params}`, {
        headers: { 'Accept': 'application/json' }
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()

      data.keywords.forEach(kw => {
        const checkbox = this.keywordCheckboxTargets.find(target => (
          target.dataset.platform === 'twitter' && target.dataset.keywordId === kw.id.toString()
        ))
        if (!checkbox?.checked) return

        this.series.set(`tw_${kw.id}`, {
          name: `${kw.name} [X]`,
          rawName: kw.name,
          metrics: this.normalizeMetrics(kw.metrics)
        })
      })
      this.refreshChart()
    } catch (error) {
      console.error('X keyword data fetch error:', error)
      keywordIds.forEach(id => this.setError(id))
    } finally {
      keywordIds.forEach(id => this.setLoading(id, false))
    }
  }

  async fetchInstData(keywordId, lang = null) {
    const abortKey = `inst_${keywordId}`
    if (this.abortControllers.has(abortKey)) {
      this.abortControllers.get(abortKey).abort()
    }

    const abortController = new AbortController()
    this.abortControllers.set(abortKey, abortController)

    this.setLoading(keywordId, true)

    await this.instQueue.enqueue(async () => {
      const params = new URLSearchParams({
        id: keywordId,
        fromDate: this.fromDateValue,
        toDate: this.toDateValue
      })
      const selectedLang = lang || this.getSelectedLang()
      if (selectedLang) params.append('lang', selectedLang)

      try {
        const response = await fetch(`${this.instDataUrlValue}?${params}`, {
          signal: abortController.signal,
          headers: { 'Accept': 'application/json' }
        })
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const data = await response.json()

        const kw = data.keyword
        const seriesData = {
          name: `${kw.name} [IG]`,
          rawName: kw.name,
          metrics: this.normalizeMetrics(kw.metrics)
        }
        this.series.set(abortKey, seriesData)
        this.refreshChart()
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(`Instagram keyword ${keywordId} fetch error:`, error)
          this.setError(keywordId)
        }
      } finally {
        this.abortControllers.delete(abortKey)
        this.setLoading(keywordId, false)
      }
    })
  }

  // --- チャート更新 ---

  removeKeywordData(keywordId) {
    this.series.delete(`tw_${keywordId}`)
    this.series.delete(`inst_${keywordId}`)
    const abortKey = `inst_${keywordId}`
    if (this.abortControllers.has(abortKey)) {
      this.abortControllers.get(abortKey).abort()
      this.abortControllers.delete(abortKey)
    }
    this.refreshChart()
  }

  refreshChart() {
    const allSeries = []
    const colors = []
    const dashArray = []

    if (this.mode === "share") {
      this.buildShareSeries(this.shareMetric).forEach(entry => {
        allSeries.push({ name: entry.name, data: entry.data })
        colors.push(this.colorForSeries(entry.key))
        dashArray.push(0)
      })
    } else {
      const checkedMetrics = this.currentMetrics()
      Array.from(this.series.entries()).forEach(([seriesKey, series]) => {
        checkedMetrics.forEach(metricKey => {
          const metricData = series.metrics[metricKey]
          if (!metricData) return

          allSeries.push({
            name: this.buildSeriesName(series, metricKey),
            data: metricData
          })
          colors.push(this.colorForSeries(seriesKey))
          dashArray.push(this.metricDashArrays[metricKey] || 0)
        })
      })
    }

    this.chart.updateOptions({
      colors: colors,
      stroke: {
        width: this.mode === "share" ? 1 : 2,
        curve: 'straight',
        dashArray: dashArray
      },
      yaxis: this.currentYAxisOptions()
    })
    this.chart.updateSeries(allSeries)
    this.updateSummaries()
  }

  // 日次シェア%をJS側で計算する（積み上げには全系列の日付を揃える必要があるため、和集合の日付に0埋めで整列する）
  buildShareSeries(metricKey) {
    const entries = Array.from(this.series.entries()).filter(([, s]) => s.metrics[metricKey])
    const timestampSet = new Set()
    entries.forEach(([, s]) => s.metrics[metricKey].forEach(point => timestampSet.add(point.x)))
    const timestamps = Array.from(timestampSet).sort((a, b) => a - b)

    const valueMaps = entries.map(([, s]) => new Map(s.metrics[metricKey].map(point => [point.x, point.y || 0])))
    const totals = new Map(timestamps.map(ts => [
      ts,
      valueMaps.reduce((sum, byTimestamp) => sum + (byTimestamp.get(ts) || 0), 0)
    ]))

    return entries.map(([key, s], index) => ({
      key,
      name: s.name,
      data: timestamps.map(ts => {
        const total = totals.get(ts)
        const value = valueMaps[index].get(ts) || 0
        return { x: ts, y: total > 0 ? Math.round(value / total * 1000) / 10 : 0 }
      })
    }))
  }

  // --- サマリー表示（タイル・SOV・色ドット） ---

  updateSummaries() {
    this.renderChips()
    this.updateTiles()
    this.renderSov()
    this.updateDots()
    this.updateSelectionCounter()
    this.updateTrendLabels()
  }

  renderChips() {
    if (!this.hasChipsTarget) return

    this.chipsTarget.textContent = ''
    const checkedBoxes = this.keywordCheckboxTargets.filter(cb => cb.checked)

    checkedBoxes.forEach(checkbox => {
      const keywordId = checkbox.dataset.keywordId
      const prefix = checkbox.dataset.platform === 'twitter' ? 'tw_' : 'inst_'
      const name = checkbox.closest('label')?.dataset.keywordName || keywordId

      const chip = document.createElement('span')
      chip.className = 'inline-flex items-center gap-1.5 border border-base-200 rounded-full pl-2.5 pr-1.5 py-1 text-sm bg-base-100'

      const dot = document.createElement('span')
      dot.className = 'size-2 rounded-full shrink-0'
      dot.style.backgroundColor = this.colorForSeries(`${prefix}${keywordId}`)

      const label = document.createElement('span')
      label.className = 'max-w-40 truncate'
      label.textContent = name
      label.title = name

      const remove = document.createElement('button')
      remove.type = 'button'
      remove.className = 'flex items-center justify-center size-4.5 rounded-full bg-base-200 cursor-pointer hover:bg-base-300 text-base-content/60 shrink-0'
      remove.setAttribute('aria-label', `${name}を比較から外す`)
      remove.innerHTML = '<span class="iconify lucide--x size-3"></span>'
      remove.addEventListener('click', () => this.removeKeyword(keywordId))

      chip.append(dot, label, remove)
      this.chipsTarget.appendChild(chip)
    })

    if (this.hasChipsEmptyTarget) {
      this.chipsEmptyTarget.classList.toggle('hidden', checkedBoxes.length > 0)
    }
  }

  updateTiles() {
    this.metricTileTargets.forEach(tile => {
      const metric = tile.dataset.metric
      const totals = Array.from(this.series.values())
        .filter(series => series.metrics[metric])
        .map(series => ({
          name: series.rawName || series.name,
          total: series.metrics[metric].reduce((sum, point) => sum + (point.y || 0), 0)
        }))
      const sum = totals.reduce((acc, entry) => acc + entry.total, 0)
      const top = totals.sort((a, b) => b.total - a.total)[0]

      tile.querySelector('[data-tile-total]').textContent = totals.length ? this.formatNumber(sum) : '—'
      tile.querySelector('[data-tile-top]').textContent = top ? `最多: ${top.name}` : 'キーワード未選択'

      const isActive = this.mode === "share" ? this.shareMetric === metric : this.activeMetrics.has(metric)
      TILE_ACTIVE_CLASSES.forEach(cls => tile.classList.toggle(cls, isActive))
      TILE_INACTIVE_CLASSES.forEach(cls => tile.classList.toggle(cls, !isActive))
      tile.setAttribute('aria-pressed', isActive)
      const activeLabel = tile.querySelector('[data-tile-active-label]')
      if (activeLabel) activeLabel.classList.toggle('hidden', !isActive)
    })
  }

  renderSov() {
    if (!this.hasSovPanelTarget) return

    if (this.mode !== "share") {
      this.sovPanelTarget.classList.add('hidden')
      return
    }
    this.sovPanelTarget.classList.remove('hidden')
    this.sovMetricLabelTarget.textContent = `指標: ${this.metricLabels[this.shareMetric] || this.shareMetric}`

    const totals = Array.from(this.series.entries())
      .filter(([, series]) => series.metrics[this.shareMetric])
      .map(([key, series]) => ({
        key,
        name: series.rawName || series.name,
        total: series.metrics[this.shareMetric].reduce((sum, point) => sum + (point.y || 0), 0)
      }))
      .sort((a, b) => b.total - a.total)
    const sum = totals.reduce((acc, entry) => acc + entry.total, 0)

    this.sovBarsTarget.textContent = ''
    if (totals.length === 0 || sum === 0) {
      const hint = document.createElement('p')
      hint.className = 'text-sm text-base-content/50 py-6 text-center'
      hint.textContent = totals.length === 0 ? '左のリストからキーワードを選択してください' : '対象期間のデータがありません'
      this.sovBarsTarget.appendChild(hint)
      return
    }

    const maxShare = totals[0].total / sum
    totals.forEach((entry, index) => {
      const share = entry.total / sum * 100
      const width = maxShare > 0 ? (entry.total / sum / maxShare) * 100 : 0

      const row = document.createElement('div')
      row.className = 'grid grid-cols-[20px_110px_minmax(0,1fr)_72px] items-center gap-2.5 my-2'

      const rank = document.createElement('div')
      rank.className = `text-xs font-bold text-center tabular-nums ${index === 0 ? 'text-primary' : 'text-base-content/40'}`
      rank.textContent = index + 1

      const label = document.createElement('div')
      label.className = 'text-[13px] text-right truncate'
      label.textContent = entry.name
      label.title = entry.name

      const track = document.createElement('div')
      track.className = 'bg-base-200 rounded h-5 overflow-hidden'
      const fill = document.createElement('div')
      fill.className = 'h-full rounded text-white text-[11px] font-bold flex items-center justify-end pr-1.5 min-w-9'
      fill.style.width = `${Math.max(width, 6)}%`
      fill.style.backgroundColor = this.colorForSeries(entry.key)
      fill.textContent = `${share.toFixed(1)}%`
      track.appendChild(fill)

      const value = document.createElement('div')
      value.className = 'text-xs text-base-content/60 tabular-nums text-right'
      value.textContent = this.formatNumber(entry.total)

      row.append(rank, label, track, value)
      this.sovBarsTarget.appendChild(row)
    })
  }

  updateDots() {
    this.keywordCheckboxTargets.forEach(checkbox => {
      const dot = checkbox.closest('label')?.querySelector('[data-kw-dot]')
      if (!dot) return

      if (checkbox.checked) {
        const prefix = checkbox.dataset.platform === 'twitter' ? 'tw_' : 'inst_'
        dot.style.backgroundColor = this.colorForSeries(`${prefix}${checkbox.dataset.keywordId}`)
      } else {
        dot.style.backgroundColor = ''
      }
    })
  }

  updateSelectionCounter() {
    if (!this.hasSelectionCounterTarget) return

    const checkedCount = this.keywordCheckboxTargets.filter(cb => cb.checked).length
    this.selectionCounterTarget.textContent = `${checkedCount}/${this.maxKeywordsValue}`
  }

  updateTrendLabels() {
    if (this.hasTrendTitleTarget) {
      this.trendTitleTarget.textContent = this.mode === "share" ? 'シェア推移' : '日次推移'
    }
    if (this.hasTrendMetricLabelTarget) {
      const labels = this.currentMetrics().map(metric => this.metricLabels[metric] || metric)
      this.trendMetricLabelTarget.textContent = `指標: ${labels.join(' / ')}`
    }
  }

  // --- ユーティリティ ---

  currentMetrics() {
    if (this.mode === "share") return [this.shareMetric]

    return Array.from(this.activeMetrics)
  }

  currentYAxisOptions() {
    const labelStyle = { colors: '#374151', fontSize: '12px', fontFamily: 'inherit' }
    if (this.mode === "share") {
      return {
        show: true,
        min: 0,
        max: 100,
        tickAmount: 4,
        title: { text: 'シェア（%）' },
        labels: {
          show: true,
          style: labelStyle,
          formatter: function(value) {
            return `${Math.round(value)}%`
          }
        }
      }
    }

    return {
      show: true,
      title: { text: this.currentYAxisTitle() },
      labels: {
        show: true,
        style: labelStyle,
        formatter: function(value) {
          return new Intl.NumberFormat('ja-JP').format(value)
        }
      }
    }
  }

  currentYAxisTitle() {
    const checkedMetrics = this.currentMetrics()
    if (checkedMetrics.length === 1) return this.metricLabels[checkedMetrics[0]] || '値'

    return '値'
  }

  buildSeriesName(series, metricKey) {
    if (this.currentMetrics().length <= 1) return series.name

    return `${series.name} / ${this.metricLabels[metricKey] || metricKey}`
  }

  colorForSeries(seriesKey) {
    const seriesSuffix = seriesKey.split('_').slice(1).join('_')
    const index = this.keywordCheckboxTargets.findIndex(checkbox => (
      checkbox.dataset.keywordId === seriesSuffix &&
      seriesKey.startsWith(checkbox.dataset.platform === 'twitter' ? 'tw_' : 'inst_')
    ))

    if (index < 0) {
      const seriesKeys = Array.from(this.series.keys())
      return this.palette[seriesKeys.indexOf(seriesKey) % this.palette.length]
    }

    return this.palette[index % this.palette.length]
  }

  formatNumber(value) {
    return new Intl.NumberFormat('ja-JP').format(value)
  }

  normalizeMetrics(metrics = {}) {
    return Object.entries(metrics).reduce((normalized, [key, values]) => {
      normalized[key] = values.map(d => ({ x: new Date(d.x).getTime(), y: d.y }))
      return normalized
    }, {})
  }

  getSelectedLang() {
    const langRadio = this.element.querySelector('input[name="inst_lang"]:checked')
    return langRadio ? langRadio.value : 'ja'
  }

  setLoading(keywordId, isLoading) {
    const indicator = this.element.querySelector(`[data-loading-keyword="${keywordId}"]`)
    if (indicator) {
      // エラー表示からの復元
      if (isLoading && indicator.querySelector('.text-error')) {
        indicator.innerHTML = '<span class="loading loading-spinner loading-xs"></span>'
      }
      indicator.classList.toggle('hidden', !isLoading)
    }
  }

  setError(keywordId) {
    const indicator = this.element.querySelector(`[data-loading-keyword="${keywordId}"]`)
    if (indicator) {
      indicator.innerHTML = '<span class="text-error text-xs">エラー</span>'
      indicator.classList.remove('hidden')
    }
  }
}
